import { formatEther } from 'ethers'

export const CHALLENGE_STATE = {
  PENDING: 0,
  ACTIVE: 1,
  ENDED: 2,
  REFUNDED: 3,
} as const

export const CHALLENGE_TYPE = {
  ENTRY_FEE: 0,
  PROMO_TOKEN: 1,
  PROMO_PHYSICAL: 2,
} as const

// How participants prove each round. The on-chain proof is just a hash commitment, so the
// proof type is purely a frontend / off-chain concern (no contract involvement).
export const PROOF_TYPES = ['checkin', 'text', 'photo'] as const
export type ProofType = (typeof PROOF_TYPES)[number]
export const DEFAULT_PROOF_TYPE: ProofType = 'photo'

export function getProofTypeLabel(t: ProofType): string {
  switch (t) {
    case 'checkin':
      return '체크인 (버튼)'
    case 'text':
      return '텍스트'
    case 'photo':
      return '사진'
  }
}

export function getProofTypeDesc(t: ProofType): string {
  switch (t) {
    case 'checkin':
      return '버튼 한 번으로 “오늘 완료” 체크'
    case 'text':
      return '짧은 글로 인증 기록'
    case 'photo':
      return '사진 업로드로 인증'
  }
}

export type ChallengeStatus = 'upcoming' | 'active' | 'ended' | 'refunded'
export type FilterTab = 'all' | 'active' | 'upcoming' | 'ended'
export type TypeFilter = 'all' | '0' | '1' | '2'

export interface ChallengeListItem {
  address: string
  name: string
  creator: string
  challengeType: number
  state: number
  startTime: number
  endTime: number
  entryFee: bigint
  winnerCount: number
  participantCount: number
  maxParticipants: number
  minParticipants: number
  emoji?: string | null
  color?: string | null
}

export interface ChallengeDetail extends ChallengeListItem {
  roundDeadlines: number[]
  isParticipant: boolean
  isRefunded?: boolean
  currentRoundDone?: boolean
  description: string | null
  proofType?: ProofType
}

export function getChallengeStatus(challenge: Pick<ChallengeListItem, 'state'>): ChallengeStatus {
  switch (challenge.state) {
    case CHALLENGE_STATE.REFUNDED:
      return 'refunded'
    case CHALLENGE_STATE.ENDED:
      return 'ended'
    case CHALLENGE_STATE.ACTIVE:
      // '진행 중' means the challenge has actually been activated on-chain (proofs open).
      return 'active'
    default:
      // PENDING: not yet activated — still accepting participants ('시작 예정').
      // It becomes ACTIVE only when someone calls activate() after the start time.
      return 'upcoming'
  }
}

export function getChallengeStatusLabel(status: ChallengeStatus): string {
  switch (status) {
    case 'upcoming':
      return '시작 예정'
    case 'active':
      return '진행 중'
    case 'ended':
      return '종료'
    case 'refunded':
      return '환불됨'
  }
}

export function getChallengeTypeName(type: number): string {
  switch (type) {
    case CHALLENGE_TYPE.PROMO_TOKEN:
      return '홍보-토큰'
    case CHALLENGE_TYPE.PROMO_PHYSICAL:
      return '홍보-실물'
    default:
      return '참가비'
  }
}

// Returns the ETH amount as a plain number string (no unit). Callers append " ETH".
// Uses exact bigint→decimal conversion so tiny amounts (e.g. 0.000001) are not rounded to 0,
// then trims trailing zeros / a dangling decimal point for clean display.
export function formatWeiToEth(wei: bigint): string {
  return formatEther(wei).replace(/\.?0+$/, '') || '0'
}

export function formatAddress(address: string): string {
  return `${address.slice(0, 6)}...${address.slice(-4)}`
}

export function formatTimestamp(ts: number): string {
  return new Date(ts * 1000).toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}

// HH:MM (24h) — used to show the exact check-in time.
export function formatTime(ts: number): string {
  return new Date(ts * 1000).toLocaleTimeString('ko-KR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

export function getRemainingText(endTime: number): string {
  const now = Date.now() / 1000
  const diff = endTime - now
  if (diff <= 0) return '종료됨'
  const days = Math.floor(diff / 86400)
  if (days > 0) return `D-${days}`
  const hours = Math.floor(diff / 3600)
  return `${hours}시간 남음`
}

export function getCurrentRound(roundDeadlines: number[]): number {
  const now = Date.now() / 1000
  for (let i = 0; i < roundDeadlines.length; i++) {
    if (now < roundDeadlines[i]) return i
  }
  return roundDeadlines.length
}

/**
 * Generate per-round deadline Unix timestamps from date range + certification cycle.
 * Dates are interpreted as local time; timestamps are UTC-based (via JS Date).
 */
export function generateRoundDeadlines(
  startDate: string, // YYYY-MM-DD
  endDate: string, // YYYY-MM-DD
  certCycle: 'daily' | 'weekly',
  deadlineTime: string, // HH:MM (local time)
): number[] {
  const [hStr, mStr] = deadlineTime.split(':')
  const hours = parseInt(hStr, 10)
  const minutes = parseInt(mStr, 10)
  const deadlines: number[] = []

  const end = new Date(endDate + 'T00:00:00')
  const step = certCycle === 'daily' ? 1 : 7
  const current = new Date(startDate + 'T00:00:00')

  while (current <= end) {
    const dl = new Date(current)
    dl.setHours(hours, minutes, 0, 0)
    deadlines.push(Math.floor(dl.getTime() / 1000))
    current.setDate(current.getDate() + step)
  }

  return deadlines
}
